import { PlayCircleOutlined } from '@ant-design/icons';
import { Button, Progress } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface AutoNextOverlayProps {
  visible: boolean;
  nextTitle: string;
  onCancel: () => void;
  onFinish: () => void;
}

const AutoNextOverlay = ({
  visible,
  nextTitle,
  onCancel,
  onFinish,
}: AutoNextOverlayProps) => {
  const [timeLeft, setTimeLeft] = useState(5);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!visible) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          onFinish();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [visible, onFinish]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className='absolute inset-0 flex flex-col items-center justify-center
            bg-black/80 text-white z-50 px-4'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
        >
          <motion.div
            initial={{ y: 25, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.35 }}
            className='flex flex-col items-center space-y-2 text-center'
          >
            <div className='text-xs sm:text-sm md:text-base font-medium opacity-80'>
              Up next
            </div>
            <div className='text-base sm:text-lg md:text-2xl font-semibold truncate max-w-[220px] sm:max-w-xs md:max-w-md'>
              {nextTitle}
            </div>
          </motion.div>

          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className='flex flex-col items-center justify-center mt-6 sm:mt-8 space-y-4 sm:space-y-6'
          >
            <Progress
              type='circle'
              percent={((5 - timeLeft) / 5) * 100}
              size={80}
              strokeColor='#FF5612'
              trailColor='rgba(255,255,255,0.2)'
              steps={5}
              format={() => (
                <PlayCircleOutlined style={{ fontSize: 24, color: '#fff' }} />
              )}
            />

            <Button
              ghost
              size='small'
              onClick={onCancel}
              className='rounded-md font-medium px-4 sm:px-6 py-1 border-gray-700 text-gray-300'
            >
              Cancel
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AutoNextOverlay;
